import { apiClient } from './api';

export interface CorporateCardUsageItem {
  id: number;
  usageId: number;
  employeeId?: number;
  employeeName: string;
  usageDate: string;
  merchant: string;
  amount: number;
  description?: string;
  memo?: string;
  employee?: {
    id: number;
    name: string;
    bankName?: string;
    bankAccount?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeSummary {
  name: string;
  total: number;
  employee?: {
    id: number;
    name: string;
    bankName?: string;
    bankAccount?: string;
  };
}

export interface CorporateCardUsage {
  id: number;
  year: number;
  month: number;
  sourceFile?: string;
  statementFile?: string;
  totalAmount: number;
  employeeCount: number;
  transferFileUrl?: string;
  items: CorporateCardUsageItem[];
  employeeSummary?: EmployeeSummary[];
  createdAt: string;
  updatedAt: string;
}

export const corporateCardApi = {
  // 전체 목록 조회
  getAll: async (): Promise<CorporateCardUsage[]> => {
    return apiClient.getList<CorporateCardUsage>('/corporate-card');
  },

  // 특정 법인카드 사용내역 조회
  getById: async (id: number): Promise<CorporateCardUsage> => {
    return apiClient.get<CorporateCardUsage>(`/corporate-card/${id}`);
  },

  // 엑셀 업로드
  upload: async (file: File, year: number, month: number): Promise<{ success: boolean; message: string; usage?: CorporateCardUsage; multiMonth?: boolean; results?: { year: number; month: number; itemCount: number }[] }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('year', year.toString());
    formData.append('month', month.toString());

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/corporate-card/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Upload failed');
    }

    return response.json();
  },

  // 청구명세서 업로드
  uploadStatement: async (id: number, file: File): Promise<{ success: boolean; message: string; usage: CorporateCardUsage }> => {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/corporate-card/${id}/statement`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Upload failed');
    }

    return response.json();
  },

  // 항목 수정
  updateItem: async (itemId: number, data: Partial<CorporateCardUsageItem>): Promise<CorporateCardUsageItem> => {
    return apiClient.put<CorporateCardUsageItem>(`/corporate-card/item/${itemId}`, data);
  },

  // 이체파일 생성
  generateTransfer: async (id: number): Promise<{ success: boolean; message: string; filePath: string }> => {
    return apiClient.post(`/corporate-card/${id}/generate-transfer`, {});
  },

  // 이체파일 다운로드 URL
  getTransferDownloadUrl: (id: number): string => {
    return `${process.env.REACT_APP_API_URL || '/api'}/corporate-card/${id}/download-transfer`;
  },

  // 사용내역 엑셀 다운로드 URL
  getExcelDownloadUrl: (id: number): string => {
    return `${process.env.REACT_APP_API_URL || '/api'}/corporate-card/${id}/download-excel`;
  },

  // 삭제
  delete: async (id: number): Promise<void> => {
    return apiClient.delete(`/corporate-card/${id}`);
  },

  // 이체파일 이메일 전송
  sendTransferEmail: async (id: number, email?: string): Promise<{ success: boolean; message: string }> => {
    return apiClient.post(`/corporate-card/${id}/send-transfer-email`, { email });
  },
};
